import React, { useMemo } from "react";
import { Button, Modal, Table } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { getRankingColumns } from "./columns";
import { useRankingMetrics } from "./hooks/useRankingMetrics";
import { normalizeGeneralMetricRow } from "./utils/rankingMetrics";
import { formatDisplayDate, formatNumber } from "../utils/eventFormatters";
import classes from "./RankingEventModal.module.scss";

const RankingEventModal = ({ event, open, onClose }) => {
  const {
    rankingRows,
    lastEvaluatedKey,
    isLoadingMetrics,
    isLoadingMoreMetrics,
    hasLoadingError,
    loadRankingMetrics,
  } = useRankingMetrics({ event, open });

  const rankingSummary = useMemo(() => {
    const eventMetrics = event?.originalEvent?.metrics || {};
    const periodStart = formatDisplayDate(
      event?.originalEvent?.start_date || event?.originalEvent?.startDate
    );
    const periodEnd = formatDisplayDate(
      event?.originalEvent?.expiration_date ||
        event?.originalEvent?.end_date ||
        event?.originalEvent?.endDate
    );
    const period =
      event?.startDate && event?.endDate
        ? `${event.startDate} - ${event.endDate}`
        : [periodStart, periodEnd].filter(Boolean).join(" - ");

    return {
      totalParticipants: formatNumber(eventMetrics.participations),
      finishedParticipants: formatNumber(eventMetrics.participations_done),
      gamesQuantity: rankingRows.length ? formatNumber(rankingRows.length) : "-",
      period: period || "-",
    };
  }, [event, rankingRows]);

  const rankingTableRows = useMemo(() => {
    const eventMetrics = event?.originalEvent?.metrics;

    return eventMetrics
      ? [normalizeGeneralMetricRow(eventMetrics, event?.id), ...rankingRows]
      : rankingRows;
  }, [event, rankingRows]);

  const columns = useMemo(
    () => getRankingColumns(rankingTableRows),
    [rankingTableRows]
  );

  const hasMoreRows = Boolean(lastEvaluatedKey?.statistic);
  const title = `RANKING ${(event?.name || "PRIMAVERA 2026").toUpperCase()}`;

  return (
    <Modal
      className={classes.RankingModal}
      closeIcon={<CloseOutlined className={classes.CloseIcon} />}
      footer={null}
      onCancel={onClose}
      open={open}
      width={1212}
    >
      <div className={classes.ModalHeader}>
        <h2 className={classes.Title}>{title}</h2>
      </div>

      <div className={classes.ModalContent}>
        <div className={classes.SummaryRow}>
          <span className={classes.SummaryItem}>
            Participantes do evento:
            <strong>{rankingSummary.totalParticipants}</strong>
          </span>
          <span className={classes.SummaryItem}>
            Participantes que finalizaram:
            <strong>{rankingSummary.finishedParticipants}</strong>
          </span>
          <span className={classes.SummaryItem}>
            Quantidade jogos:
            <strong>{rankingSummary.gamesQuantity}</strong>
          </span>
          <span className={classes.Period}>{rankingSummary.period}</span>
        </div>

        <Table
          className={classes.RankingTable}
          columns={columns}
          dataSource={rankingTableRows}
          loading={isLoadingMetrics}
          locale={{
            emptyText: hasLoadingError
              ? "Não foi possível carregar as métricas."
              : "Nenhuma métrica encontrada.",
          }}
          pagination={false}
          rowKey="id"
          tableLayout="fixed"
        />

        {hasMoreRows && (
          <div className={classes.MoreButtonWrapper}>
            <Button
              className={classes.MoreButton}
              loading={isLoadingMoreMetrics}
              onClick={() => loadRankingMetrics(lastEvaluatedKey)}
            >
              VER MAIS
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RankingEventModal;
